import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export type ToastVariant = "default" | "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
  /** Optional inline action button (e.g. "View leads" -> navigate). */
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

let toastFn: (options: Omit<Toast, "id">) => string = () => {
  console.warn("Toast called before ToastProvider mounted");
  return "";
};

export function showToast(options: Omit<Toast, "id">) {
  return toastFn(options);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    toastFn = addToast;
    return () => {
      toastFn = () => {
        console.warn("Toast called after ToastProvider unmounted");
        return "";
      };
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return createPortal(
    <div
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>,
    document.body,
  );
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const { id, title, description, variant = "default", duration = 5000, action } = toast;

  useEffect(() => {
    if (duration === 0) return;
    const timer = setTimeout(() => onRemove(id), duration);
    return () => clearTimeout(timer);
  }, [id, duration, onRemove]);

  /** Left rail color — brass for neutral notices, semantic tones otherwise. */
  const railStyles: Record<ToastVariant, string> = {
    default: "bg-brass",
    success: "bg-success",
    error: "bg-danger",
    warning: "bg-warning",
    info: "bg-info",
  };

  const titleStyles: Record<ToastVariant, string> = {
    default: "text-text-primary",
    success: "text-success",
    error: "text-danger",
    warning: "text-warning",
    info: "text-info",
  };

  return (
    <div
      className={cn(
        "pointer-events-auto relative w-[min(360px,calc(100vw-2rem))] overflow-hidden",
        "rounded-lg border border-border bg-bg-card shadow-xl animate-toast-in",
      )}
      role="alert"
      aria-live={variant === "error" ? "assertive" : "polite"}
    >
      <span
        aria-hidden="true"
        className={cn("absolute inset-y-0 left-0 w-1", railStyles[variant])}
      />
      <div className="flex items-start gap-3 p-4 pl-5">
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-semibold", titleStyles[variant])}>{title}</p>
          {description && (
            <p className="mt-1 text-sm leading-relaxed text-text-secondary">{description}</p>
          )}
          {action && (
            <button
              type="button"
              onClick={() => {
                action.onClick();
                onRemove(id);
              }}
              className="mt-2 rounded-sm text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {action.label}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(id)}
          className="flex-shrink-0 rounded p-1 text-text-muted transition-colors hover:bg-bg-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="Dismiss notification"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export const toastSuccess = (title: string, description?: string) =>
  showToast({ title, description, variant: "success" });

export const toastError = (title: string, description?: string) =>
  showToast({ title, description, variant: "error" });

export const toastWarning = (title: string, description?: string) =>
  showToast({ title, description, variant: "warning" });

export const toastInfo = (title: string, description?: string) =>
  showToast({ title, description, variant: "info" });
